/* ================================================
   EUCALOO THEME — cart-drawer.js
   Cart drawer open/close, add to cart, update quantities
   ================================================ */

(() => {
  'use strict';

  /* ---- Helpers ---- */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }

  /* ---- Color name → hex map ---- */
  const COLOR_MAP = {
    red: '#E53935', coral: '#F07060', orange: '#F0A830', amber: '#FFCA28',
    yellow: '#FFD600', lime: '#B8D840', green: '#43A047', teal: '#4CBFBF',
    cyan: '#00BCD4', sky: '#29B6F6', blue: '#4A8EC2', navy: '#1A237E',
    indigo: '#5C6BC0', violet: '#7E57C2', purple: '#C088D0', pink: '#F05A96',
    fuchsia: '#E91E8C', rose: '#F48FB1', white: '#FFFFFF', black: '#000000',
    grey: '#9E9E9E', gray: '#9E9E9E', beige: '#F5F0E8', cream: '#FFFDE7',
    brown: '#795548', khaki: '#BDB76B', gold: '#FFD700', silver: '#C0C0C0',
    ivory: '#FFFFF0', lavender: '#E6E0F8', mint: '#B2EBE0',
  };

  function getColorHex(name) {
    if (!name) return '#CCCCCC';
    const lower = name.toLowerCase().trim();
    if (COLOR_MAP[lower]) return COLOR_MAP[lower];
    for (const [key, val] of Object.entries(COLOR_MAP)) {
      if (lower.includes(key)) return val;
    }
    return '#CCCCCC';
  }

  function renderVariantOptions(item) {
    if (!item.options_with_values || item.variant_title === 'Default Title') return '';
    return item.options_with_values.map(opt => {
      const isColor = /colou?r/i.test(opt.name);
      const dot = isColor
        ? `<span class="cart-item__color-dot" style="background:${getColorHex(opt.value)};" title="${opt.value}"></span>`
        : '';
      return `<span class="cart-item__option">${dot}${opt.value}</span>`;
    }).join('<span class="cart-item__option-sep">/</span>');
  }

  /* ---- Cart State ---- */
  let cartData = null;

  /* ---- DOM refs ---- */
  const drawer      = document.getElementById('CartDrawer');
  const FREE_SHIPPING_THRESHOLD = Number(drawer?.dataset.freeShippingThreshold || 5000);
  const drawerBody  = document.getElementById('CartDrawerBody');
  const countEls    = document.querySelectorAll('#CartCount, .cart-count');
  const trigger     = document.getElementById('CartDrawerTrigger');
  const closeBtn    = document.getElementById('CartDrawerClose');
  const overlay     = document.getElementById('SiteOverlay');
  const subtotalEl  = document.getElementById('CartSubtotal');
  const shippingBar = document.getElementById('CartShippingBar');
  const shippingMsg = document.getElementById('CartShippingMsg');

  /* ---- Open / Close ---- */
  function openDrawer() {
    drawer?.classList.add('open');
    trigger?.setAttribute('aria-expanded', 'true');
    overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    overlay?.addEventListener('click', closeDrawer, { once: true });
    fetchAndRender();
  }

  function closeDrawer() {
    drawer?.classList.remove('open');
    trigger?.setAttribute('aria-expanded', 'false');
    overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  trigger?.addEventListener('click', openDrawer);
  closeBtn?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

  /* ---- Fetch Cart ---- */
  async function fetchCart() {
    const res = await fetch('/cart.js');
    cartData = await res.json();
    return cartData;
  }

  /* ---- Update Cart Count ---- */
  function updateCount(count) {
    countEls.forEach(el => {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
      el.classList.add('bump');
      setTimeout(() => el.classList.remove('bump'), 400);
    });
  }

  /* ---- Render Cart ---- */
  function renderCart(cart) {
    if (!drawerBody) return;

    updateCount(cart.item_count);

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Shipping bar
    if (shippingBar && shippingMsg) {
      const pct  = Math.min(100, (cart.total_price / FREE_SHIPPING_THRESHOLD) * 100);
      const fill = shippingBar.querySelector('.cart-shipping-bar__fill');
      if (fill) fill.style.width = pct + '%';
      if (cart.total_price >= FREE_SHIPPING_THRESHOLD) {
        shippingMsg.textContent = '🎉 You\'ve unlocked free shipping!';
      } else {
        const remaining = formatMoney(FREE_SHIPPING_THRESHOLD - cart.total_price);
        shippingMsg.textContent = `Add ${remaining} more for free shipping`;
      }
    }

    if (cart.item_count === 0) {
      drawerBody.innerHTML = `
        <div class="cart-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added anything yet!</p>
          <a href="/collections/all" class="btn btn-primary">Shop Now</a>
        </div>`;
      return;
    }

    drawerBody.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__img">
          <a href="${item.url}">
            <img src="${item.featured_image?.url ? item.featured_image.url + '&width=160' : ''}" alt="${item.title}" loading="lazy" width="80" height="100">
          </a>
        </div>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${renderVariantOptions(item)}</div>` : ''}
          <div class="cart-item__footer">
            <div class="qty-selector">
              <button class="qty-btn" data-direction="down" data-key="${item.key}" aria-label="Decrease">
                <svg width="12" height="2" viewBox="0 0 12 2"><line x1="1" y1="1" x2="11" y2="1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
              <input class="qty-input" type="number" value="${item.quantity}" min="1" max="99" data-key="${item.key}" aria-label="Quantity">
              <button class="qty-btn" data-direction="up" data-key="${item.key}" aria-label="Increase">
                <svg width="12" height="12" viewBox="0 0 12 12"><line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
            </div>
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            </button>
          </div>
        </div>
      </div>`).join('');

    bindCartItemEvents();
  }

  /* ---- Fetch and Render ---- */
  async function fetchAndRender() {
    if (drawerBody) drawerBody.style.opacity = '0.5';
    const cart = await fetchCart();
    if (drawerBody) drawerBody.style.opacity = '1';
    renderCart(cart);
  }

  /* ---- Cart Item Event Bindings ---- */
  function bindCartItemEvents() {
    // Quantity buttons (cart drawer uses data-key, not generic direction)
    drawerBody?.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key      = btn.dataset.key;
        const input    = drawerBody.querySelector(`.qty-input[data-key="${key}"]`);
        const step     = btn.dataset.direction === 'up' ? 1 : -1;
        const newQty   = Math.max(0, parseInt(input?.value || '1', 10) + step);
        await updateLineItem(key, newQty);
      });
    });

    // Quantity input
    drawerBody?.querySelectorAll('.qty-input').forEach(input => {
      let timer;
      input.addEventListener('change', () => {
        clearTimeout(timer);
        timer = setTimeout(() => updateLineItem(input.dataset.key, parseInt(input.value, 10) || 0), 500);
      });
    });

    // Remove
    drawerBody?.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => updateLineItem(btn.dataset.key, 0));
    });
  }

  /* ---- Update Line Item ---- */
  async function updateLineItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await res.json();
      renderCart(cart);
    } catch (err) {
      console.error('[Eucaloo] Cart update error:', err);
    }
  }

  /* ---- Add to Cart ---- */
  async function addToCart(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  /* ---- ATC Form Handler ---- */
  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-atc-btn]');
    if (!btn) return;

    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

    btn.classList.add('loading');
    btn.disabled = true;

    try {
      await addToCart({ id: parseInt(variantId, 10), quantity });
      await fetchCart();
      updateCount(cartData?.item_count || 0);
      openDrawer();
    } catch (err) {
      const errEl = form.querySelector('[data-atc-error]');
      if (errEl) { errEl.textContent = err.message; errEl.classList.add('visible'); }
    } finally {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  });

  /* ---- Quick Add (product card) ---- */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('loading');
    try {
      await addToCart({ id: parseInt(variantId, 10), quantity: 1 });
      await fetchCart();
      updateCount(cartData?.item_count || 0);
      openDrawer();
    } catch (err) {
      console.error('[Eucaloo] Quick add error:', err);
    } finally {
      btn.classList.remove('loading');
    }
  });

  /* ---- Expose API ---- */
  window.EucalooCart = { open: openDrawer, close: closeDrawer, fetch: fetchCart, render: renderCart };
})();
